import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Avatar, Flex, Select, Tooltip } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Container, Modal, Col, Row } from 'react-bootstrap'
import { useToasts } from '../../contexts/toasts'
import { useFullScreenLoader } from '../../contexts/fullScreenLoader'
import { Environment } from '../../models/Environment'
import { Environments } from '../../api/Environments'
import AppLoader from '../../components/common/AppLoader'
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar'
import { accessLevelOptions, formatDate } from '../../utils'
import { DefaultOptionType } from 'antd/es/select'
import { PagedResults } from '../../models/generics'
import EnvironmentVariableForm from '../../components/forms/EnvironmentVariableForm'
import { EventEmitter } from '../../utils/EventEmitter'

const environmentVariablesFilters: Filters = {
  'search': { placeholder: 'Search variable...', query: 'freeText' },
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    )
  },
}

export default function EnvironmentVariables() {
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const [latestParams, setLatestParams] = useState<URLSearchParams>();

  const [environmentVariables, setEnvironmentVariables] = useState<Environment[]>();
  const [totalEnvs, setTotalEnvs] = useState<number>();
  const [error, setError] = useState(false);
  const [variable, setVariable] = useState<Environment>();
  const [editing, setEditing] = useState<number>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const initEnvironments = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Environments.getEnvironmentVariables(params).then(
      ({ data: envPages }) => {
        setEnvironmentVariables((envPages as PagedResults<Environment>).content);
        setTotalEnvs((envPages as PagedResults<Environment>).pageMetadata.totalElements);
      },
      (err) => {
        showToast('environments-error', 'Could not get Environment Variables', 'Environment Variables Error', 'danger');
        setError(true);
        setEnvironmentVariables([]);
      }
    )
  }, [showToast, latestParams]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setEnvironmentVariables(undefined);
    initEnvironments(params);
  }, [initEnvironments]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', (params: any) => {
      reset(params);
    });

    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const handleClose = useCallback(() => {
    setShowDeleteModal(false);
    setShowEditModal(false);
    setVariable(undefined);
    setEditing(undefined);
  }, []);

  const handleDelete = (env: Environment) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setVariable(env);
  }

  const renderEnvironmentVariables = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return environmentVariables!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No Environment Variables found.</span>
        </Alert>
      ) : (
        <Row className="gx-2">
          {environmentVariables!.map((env, index) => (
            <Col key={env.id} md={4}>
              <Card className="shadow-sm mb-3">
                <Flex justify="space-between" className="p-1">
                  <Badge className="shadow w-auto">{env.accessLevel}</Badge>
                  <small className="ms-1 font-sm text-muted text-end">
                    <b>Created:</b>
                    <span className="ms-1">{formatDate(env.createDateTime!)}</span>
                  </small>
                </Flex>
                <Card.Body className="p-2">
                  <Flex align="center">
                    <Avatar
                      src={env.img}
                      icon={<FontAwesomeIcon icon={solid('camera')} size="lg" />}
                      className="border me-1"
                      style={{ height: 50, width: 50 }}
                    />
                    <Col className="ms-2 overflow-hidden">
                      <h6 className="mb-1">{env.name}</h6>
                      <Tooltip title={env.description}>
                        <p className="text-secondary text-truncate mb-0">{env.description}</p>
                      </Tooltip>
                    </Col>
                    <div className="d-flex flex-row gap-2 text-end">
                      <button className="circle-button dark ms-2" onClick={handleEdit(env, index)}>
                        <FontAwesomeIcon icon={solid('pen')} />
                      </button>
                      <button className="circle-button dark" onClick={handleDelete(env)}>
                        <FontAwesomeIcon icon={solid('trash')} />
                      </button>
                    </div>
                  </Flex>
                </Card.Body>
                <div className="tags-wrapper m-1">
                  {(env.tags || []).length === 0 && <Badge className="bg-secondary" pill={true}>0 tags</Badge>}
                  {env.tags!.map((tag) => (
                    <Badge key={tag} className="me-1" pill={true}>
                      <FontAwesomeIcon icon={solid('tag')} />
                      <span className="ms-2">{tag}</span>
                    </Badge>
                  ))}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )
  };

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const handleEdit = useCallback((env: Environment, index: number) => () => {
    setEditing(index);
    setVariable(env);
    setShowEditModal(true);
  }, []);

  const handleSubmit = useCallback((values: Environment) => {
    showLoader();
    const promise = editing !== undefined
      ? Environments.updateEnvironmentVariable(variable!.id!, values)
      : Environments.createEnvironmentVariable(values);

    promise.then(
      () => {
        hideLoader();
        showToast('environment-variable-success', `Environment Variable ${editing !== undefined ? 'updated' : 'created'} successfully`, `Environment Variable ${editing !== undefined ? 'Updated' : 'Created'}`, 'success');
        handleClose();
        const params = {
          page: "1",
          size: "20",
          sort: "id",
          desc: "true",
        };
        reset(params);
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('environment-variable-error', message, `Environment Variable ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [handleClose, reset, showLoader, hideLoader, editing, showToast, variable]);

  const renderAsyncContent = () => {
    return environmentVariables
      ? renderEnvironmentVariables()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={300} />
        </div>
      );
  };

  const deleteEnvironmentVariable = useCallback(() => {
    showLoader();

    Environments.deleteEnvironmentVariable(variable!.id!)
      .then(
        () => {
          hideLoader();
          handleClose();
          const params = {
            page: "1",
            size: "20",
            sort: "id",
            desc: "true",
          };
          reset(params);
          showToast('environment-variable-success', 'Environment Variable deleted successfully', 'Environment Variable Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('environment-variable-error', message, `Environment Variable Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, variable, reset, showToast]);

  return (
    <Container fluid="md" className="mt-3">
      <AppBrowsingToolbar
        title="User Environment Variables"
        actions={
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create Environment Variable</span>
          </Button>
        }
        total={totalEnvs}
        filters={environmentVariablesFilters}
      />
      <hr />

      {renderAsyncContent()}

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? 'Edit Environment Variable' : 'New Environment Variable'}</Modal.Title>
        </Modal.Header>
        <EnvironmentVariableForm environmentVariable={variable} onSubmit={handleSubmit} onCancel={handleClose} />
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Environment Variable</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete Environment Variable <b>{variable?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteEnvironmentVariable}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
